import { permissionsMappings } from '@lmt/unicorn-shared/app/api/auth/token-decoder';
import { useRoles } from '@lmt/unicorn-shared/app/features/roles/selectors';
import { useUsers } from '@lmt/unicorn-shared/app/features/users/selectors';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { SortDirection } from '@lmt/shared-types';
import { ScrollToTop } from '@lmt/unicorn-shared/app/components/common/table/helper';
import { IUserInfo } from '@lmt/unicorn-shared/app/components/users/list/types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import LockIcon from '@lmt/unicorn-shared/app/icons/lock.svg?react';
import { snakeCase } from 'lodash';

import styles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import localStyles from '../UserList.module.scss';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useTranslation } from 'react-i18next';
import { getGroupsByPermissions } from '../../../roles/common';
import { localizePermissionGroup } from '@lmt/unicorn-shared/app/helpers/localization/translations';

type Props = {
  noResults: boolean;
  data: IUserInfo[];
  changeSorting: (sorting: { sortBy?: string; desc?: boolean }) => void;
  onSetPassword: (userId: string) => void;
  onDelete: (userId: string) => void;
  onLock: (userId: string) => void;
  onUnlock: (userId: string) => void;
  onEdit: (userId: string) => void;
  isSuperUser?: boolean;
};

const nameof = nameofFactory<IUserInfo>();

const columnStyles = {
  [nameof('username')]: {
    minWidth: '200px',
    width: '30%',
    sortable: true,
  },
  [nameof('roles')]: {
    minWidth: '60px',
    width: '8%',
    sortable: false,
  },
  [nameof('subtenants')]: {
    minWidth: '100px',
    width: '10%',
    sortable: false,
  },
  [nameof('isLocked')]: {
    minWidth: '70px',
    width: '6%',
    sortable: true,
  },

  ['actions']: {
    minWidth: '10px',
    width: '0.1%',
  },
};

export const UsersTable: FC<Props> = React.memo(function UsersTable(props) {
  const i18n = useScopedTranslation('users.list');
  const i18next = useTranslation();
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(
    null,
  );
  const claims = useClaims();
  const roles = useRoles();
  const users = useUsers();
  const allSubtenantsLabel = i18next.t(
    'users.form.select_subtenants_all_selected',
  );

  const tableRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tableRef.current !== null) {
      ScrollToTop(tableRef.current);
    }
  }, [props.data]);

  function changeSorting(field: string) {
    if (field === sortField) {
      if (sortDirection === SortDirection.Asc) {
        props.changeSorting({
          sortBy: field,
          desc: true,
        });
        setSortDirection(SortDirection.Desc);
      } else {
        props.changeSorting({
          desc: undefined,
          sortBy: undefined,
        });
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      props.changeSorting({
        sortBy: field,
        desc: false,
      });
      setSortField(field);
      setSortDirection(SortDirection.Asc);
    }
  }

  if (props.noResults) {
    return (
      <div className={styles.emptyList}>
        <div className={styles.emptyListText}>{i18n.t('empty')}</div>
      </div>
    );
  }

  const accessToAllSubtenantRoles = useMemo(() => {
    return roles.filter((x) =>
      x.permissions?.some(
        (permission) =>
          permission === permissionsMappings.accessToAllSubtenants,
      ),
    );
  }, [roles]);

  const isAccessToAllSubtenants = useCallback(
    (userId: string): boolean => {
      const user = users.find((x) => x.id === userId);
      return user
        ? user.roles.some((role) =>
            accessToAllSubtenantRoles.some((x) => x.id === role),
          )
        : false;
    },
    [roles, users],
  );

  return (
    <TableContainer
      ref={tableRef}
      className={styles.tableContainer}
      component={Paper}
    >
      <Table stickyHeader className={styles.table}>
        <TableHead>
          <TableRow>
            {Object.keys(columnStyles).map((columnKey) => (
              <TableCell
                key={`${columnKey}`}
                onClick={() =>
                  columnStyles[columnKey].sortable && changeSorting(columnKey)
                }
                className={
                  columnStyles[columnKey].sortable
                    ? styles.sortableHeader
                    : styles.header
                }
                style={columnStyles[columnKey]}
              >
                <TableSortLabel
                  active={sortField === columnKey}
                  direction={
                    sortField === columnKey
                      ? sortDirection === SortDirection.Asc
                        ? 'asc'
                        : 'desc'
                      : undefined
                  }
                  disabled={!columnStyles[columnKey].sortable}
                  style={{ userSelect: 'none' }}
                  className={styles.headerCell}
                >
                  {i18n.t(`columns.${snakeCase(columnKey)}` as any)}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {props.data.map((user) => (
            <TableRow
              key={user.id}
              hover
              onClick={() => {
                if (props.isSuperUser) {
                  return;
                }
                props.onEdit(user.id);
              }}
            >
              <TableCell className={styles.tableCell}>
                <div className={localStyles.userName}>
                  {user.lastName} {user.firstName} ({user.username})
                </div>
                <div className={localStyles.permissions}>
                  {getGroupsByPermissions(user.permissions)
                    .map((x) => localizePermissionGroup(x.name))
                    .sort((a, b) => a.localeCompare(b))
                    .join(', ')}
                </div>
              </TableCell>
              <TableCell className={styles.tableCell}>
                {user.roles
                  .map(
                    (role) =>
                      i18next.t(
                        `roles.list.default_roles.${snakeCase(role)}`,
                        role,
                      ) as string,
                  )
                  .sort((a, b) => a.localeCompare(b))
                  .join(', ')}
              </TableCell>
              <TableCell className={styles.tableCell}>
                {!isAccessToAllSubtenants(user.id)
                  ? user.subtenants.map((subtenant) => (
                      <div key={subtenant}>{subtenant}</div>
                    ))
                  : allSubtenantsLabel}
              </TableCell>
              <TableCell>
                {user.isLocked ? (
                  <div className={localStyles.lock}>
                    <LockIcon className={localStyles.lockIcon} />
                    <span>{i18n.t('lock_cell_text')}</span>
                  </div>
                ) : (
                  ''
                )}
              </TableCell>
              <TableCell
                // classname was removed because Option cell was aligned to top (not center) at Users Table
                // className={styles.optionCell}
                onClick={(event: any) => event.stopPropagation()}
              >
                <TableDotMenu
                  options={[
                    {
                      title: i18n.t('options.set_new_password_button'),
                      onClick: () => props.onSetPassword(user.id),
                      isHidden:
                        user.isLocked || !claims.permissions.resetPassword,
                    },
                    {
                      title: i18n.t('options.unlock_button'),
                      onClick: () => props.onUnlock(user.id),
                      isHidden: props.isSuperUser || !user.isLocked,
                    },
                    {
                      title: i18n.t('options.lock_button'),
                      onClick: () => props.onLock(user.id),
                      isHidden: props.isSuperUser || user.isLocked,
                    },
                    {
                      title: i18n.t('options.edit_button'),
                      onClick: () => props.onEdit(user.id),
                      isHidden: props.isSuperUser,
                    },
                    {
                      title: i18n.t('options.delete_button'),
                      onClick: () => props.onDelete(user.id),
                      isDeleteItem: true,
                      isUnderLine: true,
                      isHidden: props.isSuperUser,
                    },
                  ]}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
